import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CalendarDays, Clock, Loader2, CheckCircle2, MapPin, User, Phone as PhoneIcon,
  Mail, MessageSquareText, ChevronDown, Info,
} from "lucide-react";
import { SITE } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slot options 10:00 → 19:00 (last start slot 7 PM)
const SLOTS = [
  { v: "10:00", label: "10:00 AM" },
  { v: "11:00", label: "11:00 AM" },
  { v: "12:00", label: "12:00 PM" },
  { v: "13:00", label: "1:00 PM" },
  { v: "14:00", label: "2:00 PM" },
  { v: "15:00", label: "3:00 PM" },
  { v: "16:00", label: "4:00 PM" },
  { v: "17:00", label: "5:00 PM" },
  { v: "18:00", label: "6:00 PM" },
  { v: "19:00", label: "7:00 PM" },
];

function fmtDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function prettyDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function SiteVisitScheduler() {
  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d;
  }, []);
  const minDateStr = fmtDateInput(today);
  const maxDateStr = fmtDateInput(maxDate);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    code: "+91",
    email: "",
    notes: "",
    visit_date: "",
    visit_time: "",
    honeypot: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.honeypot) return "Spam detected";
    if (!form.name.trim() || form.name.trim().length < 2)
      return "Please enter your full name";
    if (!/^\d{7,15}$/.test(form.phone.trim()))
      return "Please enter a valid phone number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email";
    if (!form.visit_date) return "Please choose a preferred date";
    if (!form.visit_time) return "Please choose a time slot";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setStatus("sending");
    try {
      const { data } = await axios.post(`${API}/site-visits`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        code: form.code,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
        visit_date: form.visit_date,
        visit_time: form.visit_time,
        honeypot: form.honeypot,
      });
      setResult(data);
      setStatus("success");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.detail ||
          "Could not save your booking. Please try again or call us."
      );
      setStatus("error");
    }
  };

  return (
    <section id="site-visit" className="py-16 md:py-24 bg-[#fafaf7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="uppercase tracking-[0.3em] text-[#C9A24B] text-xs mb-2">
            Book Your Visit
          </div>
          <h2 className="section-heading text-3xl md:text-4xl">
            Schedule A Site Visit
          </h2>
          <span className="gold-divider" />
          <p className="section-subheading max-w-2xl mx-auto">
            Pick a date &amp; slot that works for you. Our expert will meet you at the
            sales lounge with pricing, floor plans and a personalised walkthrough.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left: benefits card */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="rounded-lg overflow-hidden shadow-lg relative">
              <img
                src="https://images.unsplash.com/photo-1611094016919-36b65678f3d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2OTU0MDE3fDA&ixlib=rb-4.1.0&q=85"
                alt="Godrej Elaris Sales Lounge"
                className="w-full h-56 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-xs uppercase tracking-widest text-[#C9A24B]">
                  Sales Lounge
                </div>
                <div className="font-serif-display text-xl font-bold">
                  Magarpatta–Mundhwa, Pune
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#ecdfc9] rounded-lg p-5">
              <h3 className="font-serif-display text-xl font-bold text-[#8B1E1E] mb-3">
                What to expect
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#faf1dc] text-[#8B1E1E] flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <b>Personal walk-through</b>
                    <div className="text-gray-500 text-xs">
                      Show-flat visit, amenities preview and best-view towers.
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#faf1dc] text-[#8B1E1E] flex items-center justify-center shrink-0">
                    <CalendarDays className="w-4 h-4" />
                  </div>
                  <div>
                    <b>Instant confirmation</b>
                    <div className="text-gray-500 text-xs">
                      Booking confirmed on email &amp; a reminder 1 hour before your visit.
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#faf1dc] text-[#8B1E1E] flex items-center justify-center shrink-0">
                    <Info className="w-4 h-4" />
                  </div>
                  <div>
                    <b>No obligation</b>
                    <div className="text-gray-500 text-xs">
                      Free consultation, latest pricing sheet &amp; floor plans on request.
                    </div>
                  </div>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-[#ecdfc9] text-xs text-gray-500">
                Prefer to talk first?{" "}
                <a
                  href={`tel:${SITE.phoneRaw}`}
                  className="text-[#8B1E1E] font-semibold hover:underline"
                >
                  Call {SITE.phone}
                </a>
              </div>
            </div>
          </aside>

          {/* Right: booking form */}
          <div className="lg:col-span-3">
            {status === "success" && result ? (
              <div className="bg-white border border-[#ecdfc9] rounded-lg p-8 text-center shadow-[0_8px_30px_rgba(139,30,30,0.08)]">
                <div className="w-16 h-16 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-9 h-9 text-green-600" />
                </div>
                <h3 className="font-serif-display text-2xl font-bold text-[#8B1E1E] mb-2">
                  Your site visit is confirmed
                </h3>
                <span className="gold-divider" />
                <div className="bg-[#faf1dc] border border-[#ecdfc9] rounded p-4 my-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#8B1E1E]">
                    Scheduled For
                  </div>
                  <div className="font-serif-display text-lg font-bold text-[#5a1212] mt-1">
                    {result.scheduled_for}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  A confirmation has been sent to your email. You'll also receive a reminder
                  1 hour before your visit.
                </p>
                <div className="text-xs text-gray-500 mb-5">
                  Booking ID: <code className="text-gray-700">{result.id}</code>
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  <a href={`tel:${SITE.phoneRaw}`} className="btn-primary">
                    <PhoneIcon className="w-4 h-4" /> Call {SITE.phone}
                  </a>
                  <button
                    onClick={() => {
                      setStatus("idle");
                      setResult(null);
                      setForm({
                        name: "",
                        phone: "",
                        code: "+91",
                        email: "",
                        notes: "",
                        visit_date: "",
                        visit_time: "",
                        honeypot: "",
                      });
                    }}
                    className="btn-gold"
                  >
                    Book Another Slot
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white border border-[#ecdfc9] rounded-lg p-6 md:p-7 shadow-[0_8px_30px_rgba(139,30,30,0.08)]"
              >
                <input
                  type="text"
                  name="hp"
                  value={form.honeypot}
                  onChange={(e) => setField("honeypot", e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                />

                {/* Date + Time */}
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <CalendarDays className="w-3.5 h-3.5 text-[#8B1E1E]" />
                      Preferred Date*
                    </label>
                    <input
                      type="date"
                      min={minDateStr}
                      max={maxDateStr}
                      value={form.visit_date}
                      onChange={(e) => setField("visit_date", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm bg-white"
                    />
                    {form.visit_date && (
                      <div className="text-[11px] text-[#8B1E1E] mt-1 font-medium">
                        {prettyDate(form.visit_date)}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-[#8B1E1E]" />
                      Time Slot* (IST)
                    </label>
                    <div className="relative">
                      <select
                        value={form.visit_time}
                        onChange={(e) => setField("visit_time", e.target.value)}
                        className="w-full appearance-none px-3 py-2.5 pr-9 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm bg-white"
                      >
                        <option value="">Select a slot</option>
                        {SLOTS.map((s) => (
                          <option key={s.v} value={s.v}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Slot pills */}
                <div className="mb-5">
                  <div className="flex flex-wrap gap-2">
                    {SLOTS.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setField("visit_time", s.v)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          form.visit_time === s.v
                            ? "bg-[#8B1E1E] text-white border-[#8B1E1E]"
                            : "bg-white text-gray-700 border-[#ecdfc9] hover:border-[#C9A24B] hover:text-[#8B1E1E]"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact fields */}
                <div className="grid gap-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-[#8B1E1E]" /> Full Name*
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm"
                      placeholder="e.g. Ravi Sharma"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <PhoneIcon className="w-3.5 h-3.5 text-[#8B1E1E]" /> Phone Number*
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={form.code}
                        onChange={(e) => setField("code", e.target.value)}
                        className="px-2 py-2.5 border border-gray-300 rounded bg-white text-sm focus:border-[#8B1E1E] outline-none"
                      >
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+65">🇸🇬 +65</option>
                        <option value="+61">🇦🇺 +61</option>
                      </select>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) =>
                          setField("phone", e.target.value.replace(/[^0-9]/g, ""))
                        }
                        className="flex-1 px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm"
                        placeholder="8087536102"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-[#8B1E1E]" /> Email (for confirmation)
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <MessageSquareText className="w-3.5 h-3.5 text-[#8B1E1E]" />
                      Anything specific? (optional)
                    </label>
                    <textarea
                      rows="2"
                      value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded focus:border-[#8B1E1E] focus:ring-1 focus:ring-[#8B1E1E] outline-none text-sm resize-none"
                      placeholder="e.g. Interested in 3 BHK, high floor..."
                    />
                  </div>
                </div>

                {error && (
                  <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary w-full justify-center mt-5 pulse-ring"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Booking...
                    </>
                  ) : (
                    <>
                      <CalendarDays className="w-4 h-4" /> Schedule My Site Visit
                    </>
                  )}
                </button>

                <p className="text-[11px] text-gray-500 text-center leading-snug mt-3">
                  By clicking submit, you agree to our{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-[#8B1E1E] underline hover:text-[#6f1616] font-medium"
                  >
                    Privacy Policy
                  </Link>{" "}
                  regarding how we collect, store, and use your lead data.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
